import React, {Fragment, useMemo} from 'react';
import {assert} from 'assert-ts';
import type {LinkAction} from 'types';
import type {IconName} from 'components/icons/types';
import type {Linking} from 'schema/types';
import type {VerifiedLinkedCatalogPostLink} from 'schemaDefinition/types';
import type {WorkMetadata} from 'services/metadata/types';
import {useLocalization} from 'localization';
import {assertLinkTargetCatalogPostType} from 'services/utils';
import {Spacer} from 'components';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {getLinkButtonIconAndTitle} from 'schema/preview/hooks';
import type {LinkedType} from '../types';

type Props = {
  title: string;
  languages?: string[];
  entityValue?: WorkMetadata;
  linked: LinkedType;
  linkingConfiguration: Linking;
};

export const LinkCatalogPostButtons: React.FC<Props> = ({
  title,
  languages,
  entityValue,
  linked,
  linkingConfiguration,
}) => {
  const localization = useLocalization();

  const actions = useMemo((): {
    linkAction: LinkAction;
    icon: IconName;
    title: string;
    handleLinkAction: () => Promise<void>;
  }[] => {
    const {entityId, entityType, link, unlink, move, copy} =
      linkingConfiguration;

    const availableActions: LinkAction[] =
      linked === 'none'
        ? link
          ? ['link']
          : move
            ? ['copy', 'move']
            : ['copy']
        : unlink
          ? ['unlink']
          : [];

    if (!availableActions.includes('copy') && copy) {
      availableActions.unshift('copy');
    }

    const actionMap: {[key in LinkAction]: typeof link} = {
      link: link,
      unlink: unlink,
      changeLink: link,
      move: move,
      copy: copy,
    };

    return availableActions.map(action => {
      const handleClick =
        action === 'unlink'
          ? assert(
              unlink,
              'LinkCatalogPostButton: unlink expected',
              linkingConfiguration,
            )
          : () => {
              const linkValue: VerifiedLinkedCatalogPostLink = {
                linkStatus: 'verified',
                entityId: assert(
                  entityId,
                  'LinkCatalogPostButton: entityId expected',
                ),
                type: assertLinkTargetCatalogPostType(entityType),
                name: title,
                languages,
                entityValue:
                  action === 'move' || action === 'copy'
                    ? entityValue
                    : undefined,
              };
              assert(
                actionMap[action],
                'LinkCatalogPostButton: action expected',
                {action},
              )(linkValue);
            };
      return {
        linkAction: action,
        ...getLinkButtonIconAndTitle(action, entityType, localization),
        handleLinkAction: () => Promise.resolve(handleClick()),
      };
    });
  }, [
    entityValue,
    languages,
    linked,
    linkingConfiguration,
    localization,
    title,
  ]);

  return actions.length > 0 ? (
    <>
      {actions.map(({title, icon, handleLinkAction}, idx) => (
        <Fragment key={title}>
          {idx > 0 ? <Spacer width={1} /> : null}
          <ActionButtonV2
            title={title}
            icon={icon}
            onClick={handleLinkAction}
          />
        </Fragment>
      ))}
    </>
  ) : null;
};
